<template>
  <div class="select-search-virtual-list">
    <div
      v-if="$slots['menu-header']"
      class="select-search-virtual-list__header"
      :class="{ 'select-search-virtual-list__header--bordered': !hasSearch }"
    >
      <slot name="menu-header" />
    </div>
    <template v-if="hasSearch">
      <tm-search
        ref="filterEl"
        class="select-search-virtual-list__search"
        :model-value="filterValue"
        :field-class="filterFieldClass"
        :placeholder="filterPlaceholder"
        :debounce="debounce"
        :size="filterSize"
        autofocus
        :aria-expanded="showDropdown"
        :aria-controls="`${instanceUid}_lb`"
        role="combobox"
        tabindex="0"
        @click.stop
        @keydown="onKeydown"
        @update:model-value="onFilter"
      />
    </template>

    <slot
      v-if="showSkeleton && loading"
      name="skeleton-list"
    >
      <tm-skeleton-select-search class="px-3" />
    </slot>

    <tm-virtual-scroll
      v-else-if="options?.length"
      ref="virtualScrollEl"
      class="select-search-virtual-list__virtual-scroll"
      :items="options"
      :min-row-height="rowHeight"
      :row-height="rowHeight"
      scrollbar-style="thumb-with-track"
      @page="onPage"
    >
      <template v-slot="{ item: opt, index }">
        <div
          :id="`${instanceUid}_${index}`"
          class="select-search-virtual-list__item"
          :class="{
            'select-search-virtual-list__item--disabled': opt.disabled,
            'select-search-virtual-list__item--active': isActive(index) && !opt.disabled,
          }"
          :aria-disabled="opt.disabled"
          :aria-selected="isActive(index) && !opt.disabled"
          role="option"
          tabindex="-1"
          @click="onClickItem(opt)"
        >
          <tm-sub-header-option
            v-if="opt.subheader"
            :class-name="subHeaderClass"
            :subheader-style="subheaderStyle"
            @click="focusFilter"
          >
            {{ opt.subheader }}
          </tm-sub-header-option>
          <slot
            v-else-if="opt.overrideOption"
            name="override-option"
            v-bind="{ opt, focused: isActive(index), focusFilter }"
          />
          <tm-default-option
            v-else
            :item-style="itemStyle"
            :class-name="itemClass"
            :disable="opt.disabled || isDisabledOptions"
            :loading="isLoadingOptions || isLoadingFirstPage"
          >
            <slot
              name="option"
              v-bind="{ opt, filterValue }"
            >
              <tm-icon
                v-if="opt.icon"
                class="neutral--text"
                :name="opt.icon"
                left
              />
              <span class="truncate">{{ opt[optionLabel] }}</span>
            </slot>
          </tm-default-option>
        </div>
      </template>
    </tm-virtual-scroll>
    <div
      v-if="isLoadingMorePage || isLoadingPage"
      class="flex flex-center my-1"
    >
      <tm-spinner color="blue" />
    </div>
    <q-item
      v-else-if="!options?.length"
      class="px-3"
    >
      <q-item-section class="text-grey">
        {{ $t('common.noResults') }}
      </q-item-section>
    </q-item>
    <slot name="menu-footer" />
  </div>
</template>

<script setup lang="ts" generic="TOption extends IBaseListOption">
import { computed, getCurrentInstance, onMounted, ref, watch } from '@/composition/vue/compositionApi'
import TmVirtualScroll from '@/components/shared/tmVirtualScroll/TmVirtualScroll.vue'
import TmSearch from '@/components/shared/TmSearch.vue'
import TmDefaultOption from '@/components/shared/options/TmDefaultOption.vue'
import TmSkeletonSelectSearch from '@/components/shared/skeletons/TmSkeletonSelectSearch.vue'
import TmSubHeaderOption from '@/components/shared/options/TmSubHeaderOption.vue'
import TmSpinner from '@/components/shared/TmSpinner.vue'
import { selectOptionLabelKey } from '@/services/forms/types'
import { keyCodes } from '@/constants/keyCodes'
import type { FieldTextSize } from '@/components/forms/fields/FieldText.vue'
import { stopAndPrevent } from '@/utils'
import type { IBaseListOption } from '@/components/forms/fields/types'

const { keyEnter, keyDown, keyUp, keyPageDown, keyPageUp, keyEsc } = keyCodes

const DEFAULT_VISIBLE_ITEMS = 10

const props = withDefaults(
  defineProps<{
    selectedIndex?: number
    showDropdown?: boolean
    filterValue?: string
    filterFieldClass?: string
    filterPlaceholder?: string
    filterSize?: FieldTextSize
    debounce?: number
    showSkeleton?: boolean
    loading?: boolean
    subheaderStyle?: 'bold' | 'caption'
    subHeaderClass?: string
    itemStyle?: 'regular' | 'semi-bold'
    itemClass?: string
    options?: TOption[]
    optionLabel?: string
    isDisabledOptions?: boolean
    isLoadingOptions?: boolean
    loadMoreItemsHandler?: () => void | Promise<void>
    isLoadingMorePage?: boolean
    isLoadingFirstPage?: boolean
    hasSearch?: boolean
    rowHeight?: number
  }>(),
  {
    selectedIndex: -1,
    filterFieldClass: 'px-3 pb-3',
    filterPlaceholder: 'Search',
    filterSize: 'medium',
    subheaderStyle: 'bold',
    itemStyle: 'regular',
    optionLabel: selectOptionLabelKey,
    isDisabledOptions: false,
    isLoadingOptions: false,
    hasSearch: true,
    rowHeight: 36,
  },
)

const emit = defineEmits<{
  select: [opt: TOption]
  'update:filter': [val: string]
  'load-more-items': []
  'create-new-item': []
}>()

const instanceUid = getCurrentInstance()?.uid ?? 'select-search-list'

const filterEl = ref()
const virtualScrollEl = ref()
const activeIndex = ref<number>(props.selectedIndex)
const isLoadingPage = ref(false)

const optionsLength = computed(() => props.options?.length ?? 0)

const listMaxHeightValue = computed(() => `${props.rowHeight * DEFAULT_VISIBLE_ITEMS}px`)

const isOptionDisabled = (index: number): boolean => {
  const option = props.options?.[index]
  return Boolean(option?.disabled) || Boolean(option?.subheader)
}

const isActive = (index: number): boolean => {
  return activeIndex.value === index
}

const scrollToItem = (index: number) => {
  if (index < 0 || !virtualScrollEl.value) {
    return
  }
  virtualScrollEl.value.scrollTo(index)
}

const getViewportItemCount = (): number => {
  const scrollElement = virtualScrollEl.value?.getScrollElement?.()
  if (!scrollElement) {
    return 10 // fallback
  }
  return Math.max(1, Math.floor(scrollElement.clientHeight / props.rowHeight))
}

const moveOptionSelection = (offset = 1, wrap = true) => {
  if (optionsLength.value === 0) {
    return
  }

  let index = activeIndex.value
  const maxIndex = optionsLength.value - 1

  // If no active item, start from beginning or end based on direction
  if (index < 0) {
    index = offset > 0 ? -1 : maxIndex + 1
  }

  let counter = 0

  do {
    index += offset
    if (wrap) {
      // Wrap around
      if (index > maxIndex) {
        index = 0
      } else if (index < 0) {
        index = maxIndex
      }
    } else {
      // Clamp to bounds
      index = Math.max(0, Math.min(index, maxIndex))
    }
    counter += 1
    if (counter >= maxIndex) {
      // prevent infinite loop
      break
    }
  } while (index !== activeIndex.value && isOptionDisabled(index))

  if (activeIndex.value !== index) {
    activeIndex.value = index
    scrollToItem(index)
  }
}

const onKeydown = (e: KeyboardEvent) => {
  if (e.code === keyEsc) {
    filterEl.value?.blur()
    return
  }

  if (![keyEnter, keyDown, keyUp, keyPageDown, keyPageUp].includes(e.code)) {
    return
  }

  // pg up, pg down - move by page size (stop at bounds)
  if (e.code === keyPageUp || e.code === keyPageDown) {
    stopAndPrevent(e)
    const pageSize = Math.floor(getViewportItemCount())
    moveOptionSelection(e.code === keyPageUp ? -pageSize : pageSize, false)
  }

  // up, down
  if (e.code === keyUp || e.code === keyDown) {
    stopAndPrevent(e)
    moveOptionSelection(e.code === keyUp ? -1 : 1)
  }

  // enter
  if (e.code === keyEnter) {
    stopAndPrevent(e)
    const index = activeIndex.value
    if (index >= 0 && index < optionsLength.value) {
      const option = props.options?.[index]
      if (option) {
        onClickItem(option)
      }
    }
  }
}

const onFilter = (value: string) => emit('update:filter', value)

const onClickItem = (opt: TOption) => {
  if (opt.subheader || opt.disabled || props.isDisabledOptions) {
    return
  }
  emit('select', opt)
}

const onPage = async () => {
  if (props.loadMoreItemsHandler) {
    try {
      isLoadingPage.value = true
      await props.loadMoreItemsHandler()
    } finally {
      isLoadingPage.value = false
    }
  }
}

const focusFilter = () => {
  filterEl.value?.focus()
}

watch(
  () => props.selectedIndex,
  (newIndex) => {
    activeIndex.value = newIndex
  },
)

watch(
  () => props.options,
  (newOptions) => {
    // Reset active index when options change (e.g., after filtering)
    const newLength = newOptions?.length ?? 0
    if (newLength === 0) {
      activeIndex.value = -1
    } else if (activeIndex.value >= newLength) {
      activeIndex.value = 0
    }
  },
)

onMounted(() => {
  requestAnimationFrame(() => {
    focusFilter()
    if (props.selectedIndex >= 0) {
      scrollToItem(props.selectedIndex)
    }
  })
})
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.select-search-virtual-list {
  //--min-width: 390px; comes from props
  display: flex;
  flex-flow: column;
  overflow: hidden;

  &__new-btn-wrapper {
    display: flex;
    justify-content: flex-start;
    height: 40px;
  }

  &__header {
    padding: 0 12px 12px;

    &--bordered {
      border-bottom: 1px solid $border-color;
      margin-bottom: 8px;
    }
  }

  &__virtual-scroll {
    @include thumb-with-track;

    max-height: var(--select-search-list-max-height, v-bind(listMaxHeightValue));
    overflow-y: auto;
  }

  &__item {
    position: relative;

    &--disabled,
    &--disabled :deep(*) {
      cursor: default;
    }

    &:not(&--disabled):not(&--active):hover {
      background-color: $gray1;
    }

    &--active {
      background-color: $gray1;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 2px;
        background-color: $blue;
      }
    }
  }
}
</style>
